import styled from 'styled-components';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import Tooltip from 'components/shared/Tooltip';
import { simpleDate } from 'lib/date';
import { ReferralSource } from 'models/ReferralSource';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import InfoIconOutline from 'svg/InfoIconOutline';

import ReferralSourceRowMenu from './ReferralSourceRowMenu';

const columnHelper = createColumnHelper<ReferralSource>();

const EmptyValue = () => <>&mdash;</>;

const ALIAS_HELP = 'Alternative names this referral source might be listed under so referrals are matched correctly.';

type ReferralSourceColumnsOptions = {
  isAdmin?: boolean;
  onEdit: (referralSource: ReferralSource) => void;
  onDelete: (referralSource: ReferralSource) => void;
};

export default function referralSourceColumns({ isAdmin, onEdit, onDelete }: ReferralSourceColumnsOptions) {
  const columns: ColumnDef<ReferralSource, any>[] = [
    columnHelper.accessor('name', {
      id: 'name',
      header: 'Referral Source',
      cell: (info) => <BodySmallBold>{info.getValue() || <EmptyValue />}</BodySmallBold>,
      size: 320,
    }),
    columnHelper.accessor('aliases', {
      id: 'aliases',
      header: () => (
        <AliasHeader>
          Also Known As
          <Tooltip text={ALIAS_HELP} position='bottom'>
            <InfoIconOutline width={14} height={14} fill={colors.black75} />
          </Tooltip>
        </AliasHeader>
      ),
      // Sorting is server-side and these live on sibling rows rather than this one, so there is no
      // single column to order by. Search does reach them.
      enableSorting: false,
      cell: (info) => {
        const aliases: string[] = info.getValue() ?? [];
        if (aliases.length === 0) return <EmptyValue />;

        return <BodySmall>{aliases.join(', ')}</BodySmall>;
      },
      size: 380,
    }),
    columnHelper.accessor('dateAdded', {
      id: 'dateAdded',
      header: 'Date Added',
      cell: (info) => <BodySmall>{simpleDate(info.getValue()) ?? <EmptyValue />}</BodySmall>,
      size: 140,
      // Descending is the first direction, so clicking the header toggles desc -> asc rather than
      // dropping to unsorted.
      sortDescFirst: true,
    }),
    columnHelper.display({
      id: 'menu',
      cell: (info) => (
        <ReferralSourceRowMenu
          referralSource={info.row.original}
          isAdmin={isAdmin}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
      size: 40,
      meta: { style: { justifyContent: 'flex-end' } },
    }),
  ];

  return columns;
}

const AliasHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
